import { getCurrentInstance, onBeforeUnmount } from "vue";
let interactModule = null;
async function loadInteract() {
  if (!interactModule) {
    interactModule = (await import("./index102.js").then((n) => n.i)).default;
  }
  return interactModule;
}
const TABLE_MAP_GRID = 10;
function useTableMap(gridSizeInput = TABLE_MAP_GRID) {
  const getGridSize = () => typeof gridSizeInput === "number" ? gridSizeInput : gridSizeInput.value;
  function snapToGrid(percentValue, containerDimension) {
    const pixels = percentValue * containerDimension / 100;
    const g = getGridSize();
    const snapped = Math.round(pixels / g) * g;
    return snapped / containerDimension * 100;
  }
  function snapSectorTablesToGrid(tables, container) {
    tables.forEach((table) => {
      table.pos_left = snapToGrid(table.pos_left ?? 0, container.width);
      table.pos_top = snapToGrid(table.pos_top ?? 0, container.height);
      table.width = snapToGrid(table.width ?? 0, container.width);
      table.height = snapToGrid(table.height ?? 0, container.height);
    });
  }
  function pixelsToPercent(pixels, containerDimension) {
    return pixels / containerDimension * 100;
  }
  function computeUpdatedPosition(table, tableEl, container) {
    const copy = JSON.parse(JSON.stringify(table));
    const x = parseFloat(tableEl.getAttribute("data-x") || "0");
    const y = parseFloat(tableEl.getAttribute("data-y") || "0");
    const g = getGridSize();
    const snap = (v) => Math.round(v / g) * g;
    const left = snap(tableEl.offsetLeft + x);
    const top = snap(tableEl.offsetTop + y);
    const width = Math.max(g, snap(tableEl.offsetWidth));
    const height = Math.max(g, snap(tableEl.offsetHeight));
    copy.pos_left = pixelsToPercent(left, container.width);
    copy.pos_top = pixelsToPercent(top, container.height);
    copy.width = pixelsToPercent(width, container.width);
    copy.height = pixelsToPercent(height, container.height);
    return copy;
  }
  function resetTransform(tableEl) {
    tableEl.setAttribute("data-x", "0");
    tableEl.setAttribute("data-y", "0");
    tableEl.style.transform = "translate(0px, 0px)";
  }
  let interactable = null;
  async function setupInteract(selector, callbacks) {
    const interact = await loadInteract();
    teardownInteract();
    interactable = interact(selector).draggable({
      autoScroll: true,
      onstart: (event) => {
        var _a;
        const target = event.target;
        target.setAttribute("data-accumulated-x", "0");
        target.setAttribute("data-accumulated-y", "0");
        const draggedId = parseInt(target.id.split("-")[1]);
        const siblingIds = ((_a = callbacks.getSelectedSiblingIds) == null ? void 0 : _a.call(callbacks, draggedId)) || [];
        target.setAttribute("data-sibling-ids", JSON.stringify(siblingIds));
      },
      onmove: (event) => {
        const target = event.target;
        const currentX = parseFloat(target.getAttribute("data-x")) || 0;
        const currentY = parseFloat(target.getAttribute("data-y")) || 0;
        let accumulatedX = parseFloat(target.getAttribute("data-accumulated-x")) || 0;
        let accumulatedY = parseFloat(target.getAttribute("data-accumulated-y")) || 0;
        accumulatedX += event.dx;
        accumulatedY += event.dy;
        const g = getGridSize();
        const gridStepsX = Math.round(accumulatedX / g);
        const gridStepsY = Math.round(accumulatedY / g);
        if (gridStepsX !== 0 || gridStepsY !== 0) {
          let deltaX = gridStepsX * g;
          let deltaY = gridStepsY * g;
          const siblingIds = JSON.parse(target.getAttribute("data-sibling-ids") || "[]");
          const siblingEls = [];
          for (const sibId of siblingIds) {
            const sibEl = document.getElementById("table-" + sibId);
            if (sibEl) siblingEls.push(sibEl);
          }
          const container = target.parentElement;
          if (container) {
            const cw = container.clientWidth;
            const ch = container.clientHeight;
            const allEls = [target, ...siblingEls];
            let minDX = -Infinity, maxDX = Infinity;
            let minDY = -Infinity, maxDY = Infinity;
            for (const el of allEls) {
              const elX = parseFloat(el.getAttribute("data-x") || "0");
              const elY = parseFloat(el.getAttribute("data-y") || "0");
              const elLeft = el.offsetLeft + elX;
              const elTop = el.offsetTop + elY;
              minDX = Math.max(minDX, -elLeft);
              minDY = Math.max(minDY, -elTop);
              maxDX = Math.min(maxDX, cw - elLeft - el.offsetWidth);
              maxDY = Math.min(maxDY, ch - elTop - el.offsetHeight);
            }
            minDX = Math.ceil(minDX / g) * g;
            minDY = Math.ceil(minDY / g) * g;
            maxDX = Math.floor(maxDX / g) * g;
            maxDY = Math.floor(maxDY / g) * g;
            deltaX = Math.max(minDX, Math.min(maxDX, deltaX));
            deltaY = Math.max(minDY, Math.min(maxDY, deltaY));
          }
          if (deltaX === 0 && deltaY === 0) {
            target.setAttribute("data-accumulated-x", String(accumulatedX));
            target.setAttribute("data-accumulated-y", String(accumulatedY));
            return;
          }
          const newX = currentX + deltaX;
          const newY = currentY + deltaY;
          target.style.transform = `translate(${newX}px, ${newY}px)`;
          target.setAttribute("data-x", String(newX));
          target.setAttribute("data-y", String(newY));
          for (const sib of siblingEls) {
            const sibX = parseFloat(sib.getAttribute("data-x") || "0") + deltaX;
            const sibY = parseFloat(sib.getAttribute("data-y") || "0") + deltaY;
            sib.style.transform = `translate(${sibX}px, ${sibY}px)`;
            sib.setAttribute("data-x", String(sibX));
            sib.setAttribute("data-y", String(sibY));
          }
          accumulatedX -= deltaX;
          accumulatedY -= deltaY;
        }
        target.setAttribute("data-accumulated-x", String(accumulatedX));
        target.setAttribute("data-accumulated-y", String(accumulatedY));
      },
      onend: (event) => {
        const tableId = parseInt(event.target.id.split("-")[1]);
        callbacks.onDragEnd(tableId);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- verbatim old cast (interactjs legacy options)
    }).resizable({
      edges: { left: true, right: true, bottom: true, top: true },
      margin: 10,
      modifiers: [
        interact.modifiers.snapSize({
          targets: [interact.snappers.grid({ x: getGridSize(), y: getGridSize() })]
        }),
        interact.modifiers.restrictEdges({
          outer: "parent",
          endOnly: false
        }),
        interact.modifiers.restrictSize({
          min: { width: 40, height: 40 }
        })
      ]
    }).on("resizemove", (event) => {
      const target = event.target;
      let x = parseFloat(target.getAttribute("data-x")) || 0;
      let y = parseFloat(target.getAttribute("data-y")) || 0;
      target.style.width = `${event.rect.width}px`;
      target.style.height = `${event.rect.height}px`;
      x += event.deltaRect.left;
      y += event.deltaRect.top;
      target.style.transform = `translate(${x}px, ${y}px)`;
      target.setAttribute("data-x", String(x));
      target.setAttribute("data-y", String(y));
      let sizeLabel = target.querySelector(".table-item__size-label");
      const labelText = `${Math.round(event.rect.width)}×${Math.round(event.rect.height)}`;
      if (!sizeLabel) {
        sizeLabel = document.createElement("div");
        sizeLabel.classList.add("table-item__size-label");
        Object.assign(sizeLabel.style, {
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "rgba(0, 0, 0, 0.75)",
          color: "#ffffff",
          fontSize: "12px",
          fontWeight: "600",
          padding: "2px 8px",
          borderRadius: "4px",
          whiteSpace: "nowrap",
          zIndex: "20",
          pointerEvents: "none"
        });
        target.appendChild(sizeLabel);
      }
      sizeLabel.innerText = labelText;
    }).on("resizeend", (event) => {
      var _a;
      (_a = event.target.querySelector(".table-item__size-label")) == null ? void 0 : _a.remove();
      const tableId = parseInt(event.target.id.split("-")[1]);
      callbacks.onResizeEnd(tableId);
    });
  }
  function teardownInteract() {
    if (interactable) {
      interactable.unset();
      interactable = null;
    }
  }
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      teardownInteract();
    });
  }
  return {
    snapToGrid,
    snapSectorTablesToGrid,
    computeUpdatedPosition,
    resetTransform,
    setupInteract,
    teardownInteract
  };
}
export {
  TABLE_MAP_GRID,
  useTableMap
};
//# sourceMappingURL=index49.js.map
